import React, { FC, useEffect, useState } from 'react';
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ReplayIcon from '@mui/icons-material/Replay';
import InfoIcon from '@mui/icons-material/Info';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import ChecklistRtlIcon from '@mui/icons-material/ChecklistRtl';
import CategoryIcon from '@mui/icons-material/Category';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { AddProfile } from 'components/dialog/profiles/AddProfile';
import { EditProfile } from 'components/dialog/profiles/EditProfile';
import { AssignPermissions } from 'components/dialog/profiles/assign_permissions/AssignPermissions';
import { AssignCategory } from 'components/dialog/assign_category/AssignCategory';
import { AssignViews } from 'components/dialog/profiles/assign_views/AssignViews';
import { AssignTags } from 'components/dialog/profiles/assign_tags/AssignTags';
import { InfoProfile } from 'components/dialog/profiles/InfoProfile';
import {
    buildUserManagementProfilesActionPath,
    buildUserManagementProfilesBasePath,
} from 'utils/userManagementProfilesRouting';

interface SubMenuProps {
    selected: any;
    rows: any;
    contextMenu: any;
    setContextMenu: (value: any) => void;
    reloading: () => void;
    permissions: any;
    views: any;
    tags: any;
    routeAction?: string | null;
}

const SubMenu: FC<SubMenuProps> = ({
    selected,
    rows,
    contextMenu,
    setContextMenu,
    reloading,
    permissions,
    views,
    tags,
    routeAction,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    const [openAdd, setOpenAdd] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openAssignPermissions, setOpenAssignPermissions] = useState(false);
    const [openAssignCategories, setOpenAssignCategories] = useState(false);
    const [openAssignViews, setOpenAssignViews] = useState(false);
    const [openAssignTags, setOpenAssignTags] = useState(false);
    const [openInfo, setOpenInfo] = useState(false);

    useEffect(() => {
        const action = routeAction || null;

        setOpenAdd(action === 'add');
        setOpenEdit(action === 'edit' && selected?.length === 1);
        setOpenAssignPermissions(action === 'assign-permissions' && selected?.length === 1);
        setOpenAssignCategories(action === 'assign-categories' && selected?.length === 1);
        setOpenAssignViews(action === 'assign-views' && selected?.length === 1);
        setOpenAssignTags(action === 'assign-tags' && selected?.length === 1);
        setOpenInfo(action === 'detail' && selected?.length === 1);
    }, [routeAction, selected]);

    const closeToBase = () => {
        if (location.pathname !== buildUserManagementProfilesBasePath()) {
            navigate(buildUserManagementProfilesBasePath());
        }
    };

    const options = [
        {
            name: t('profiles.table.context_menu.add'),
            icon: <AddIcon />,
            click: () => {
                navigate(buildUserManagementProfilesActionPath('add'));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: (JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'Auth.CreateProfile') ? false : true,
            onlyOne: false,
            dialog: (
                <AddProfile
                    open={openAdd}
                    setOpen={(value) => {
                        setOpenAdd(value);
                        if (!value) {
                            closeToBase();
                        }
                    }}
                    rows={rows}
                    reloading={reloading}
                />
            ),
        },
        {
            name: t('profiles.table.context_menu.edit'),
            icon: <EditIcon />,
            click: () => {
                navigate(buildUserManagementProfilesActionPath('edit', selected?.[0]?.id));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: (JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'Auth.UpdateProfiles') ? false : true,
            onlyOne: true,
            dialog: (
                <EditProfile
                    open={openEdit}
                    setOpen={(value) => {
                        setOpenEdit(value);
                        if (!value) {
                            closeToBase();
                        }
                    }}
                    rows={rows}
                    select={selected}
                    reloading={reloading}
                />
            ),
        },
        {
            name: t('profiles.table.context_menu.assign_permissions'),
            icon: <HowToRegIcon />,
            click: () => {
                navigate(buildUserManagementProfilesActionPath('assign-permissions', selected?.[0]?.id));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: (JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'Auth.AssignPermission') ? false : true,
            onlyOne: true,
            dialog: (
                <AssignPermissions
                    open={openAssignPermissions}
                    setOpen={(value) => {
                        setOpenAssignPermissions(value);
                        if (!value) {
                            closeToBase();
                        }
                    }}
                    select={selected}
                    reloading={reloading}
                    permissions={permissions}
                />
            ),
        },
        {
            name: t('profiles.table.context_menu.assign_categories'),
            icon: <CategoryIcon />,
            click: () => {
                navigate(buildUserManagementProfilesActionPath('assign-categories', selected?.[0]?.id));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: (JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'Auth.PatchProfileCategoryReport') ? false : true,
            onlyOne: true,
            dialog: (
                <AssignCategory
                    open={openAssignCategories}
                    setOpen={(value: boolean) => {
                        setOpenAssignCategories(value);
                        if (!value) {
                            closeToBase();
                        }
                    }}
                    profileId={selected?.[0]?.id}
                />
            ),
        },
        {
            name: t('profiles.table.context_menu.assign_views'),
            icon: <ChecklistRtlIcon />,
            click: () => {
                navigate(buildUserManagementProfilesActionPath('assign-views', selected?.[0]?.id));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: (JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'Auth.AssignView') ? false : true,
            onlyOne: true,
            dialog: (
                <AssignViews
                    open={openAssignViews}
                    setOpen={(value) => {
                        setOpenAssignViews(value);
                        if (!value) {
                            closeToBase();
                        }
                    }}
                    select={selected}
                    reloading={reloading}
                    views={views}
                />
            ),
        },
        {
            name: t('profiles.table.context_menu.assign_tags'),
            icon: <LocalOfferIcon />,
            click: () => {
                navigate(buildUserManagementProfilesActionPath('assign-tags', selected?.[0]?.id));
                setContextMenu(null);
            },
            color: '',
            divider: false,
            disabled: (JSON.parse(localStorage.getItem('permissions') || '[]') as string[]).find((permision: string) => permision === 'Auth.AssignTag') ? false : true,
            onlyOne: true,
            dialog: (
                <AssignTags
                    open={openAssignTags}
                    setOpen={(value) => {
                        setOpenAssignTags(value);
                        if (!value) {
                            closeToBase();
                        }
                    }}
                    select={selected}
                    reloading={reloading}
                    tags={tags}
                />
            ),
        },
        {
            name: t('profiles.table.context_menu.information'),
            icon: <InfoIcon />,
            click: () => {
                navigate(buildUserManagementProfilesActionPath('detail', selected?.[0]?.id));
                setContextMenu(null);
            },
            color: '',
            divider: true,
            disabled: false,
            onlyOne: true,
            dialog: (
                <InfoProfile
                    open={openInfo}
                    setOpen={(value) => {
                        setOpenInfo(value);
                        if (!value) {
                            closeToBase();
                        }
                    }}
                    selected={selected}
                />
            ),
        },
        {
            name: t('profiles.table.context_menu.reload'),
            icon: <ReplayIcon />,
            click: () => {
                reloading();
                setContextMenu(null);
            },
            color: '',
            divider: true,
            disabled: false,
            onlyOne: false,
            dialog: null,
        },
    ];

    return (
        <>
            <Menu
                open={Boolean(contextMenu)}
                onClose={() => setContextMenu(null)}
                anchorReference="anchorPosition"
                anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
                sx={{ boxShadow: 'none' }}
            >
                {options.map((option) => (
                    <div key={option.name} hidden={option.onlyOne && selected?.length !== 1}>
                        {option.divider && <Divider />}
                        <MenuItem onClick={option.click} disabled={option.disabled}>
                            <ListItemIcon>{option.icon}</ListItemIcon>
                            <ListItemText primary={option.name} slotProps={{ primary: { color: option.color } }} />
                        </MenuItem>
                    </div>
                ))}
            </Menu>

            {options.map((option) => (
                option.dialog ? <React.Fragment key={`dialog-${option.name}`}>{option.dialog}</React.Fragment> : null
            ))}
        </>
    );
};

export { SubMenu };
